/**
 * SocialPage - Sorta Social feed showing activity from followed users
 * Features:
 * - Activity feed (watched, listened, added to lists)
 * - Emoji reactions (including media-related emojis)
 * - Activity filtering by media type
 * - Friends' "Watching Now" statuses
 * - Click items to view details with add to list
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Users, Tv, Film, Mic, ThumbsUp, Heart, Laugh, Eye, Flame,
  ListPlus, Headphones, Clock, Loader2, ChevronRight, RefreshCw,
  Sparkles, TrendingUp, Star, ThumbsDown, Meh, MessageSquare, Plus,
  Calendar, ExternalLink, List, ArrowUp, ArrowDown, ListOrdered, Check, Clapperboard, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';
import { getTMDBDetails, getBookDetails, getPodcastDetails } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Emoji reactions configuration - including media-related emojis
const REACTIONS = [
  { emoji: '👍', label: 'Like' },
  { emoji: '❤️', label: 'Love' },
  { emoji: '🔥', label: 'Fire' },
  { emoji: '🍿', label: 'Popcorn' },
  { emoji: '🎬', label: 'Movie' },
  { emoji: '📺', label: 'TV' },
  { emoji: '🎧', label: 'Headphones' },
  { emoji: '😂', label: 'Haha' },
  { emoji: '😮', label: 'Wow' },
];

// Activity type icons
const ACTIVITY_ICONS = {
  watched: Eye,
  listened: Headphones,
  added_to_list: ListPlus,
  status_update: MessageSquare,
};

// Media type icons
const MEDIA_ICONS = {
  tv: Tv,
  movie: Film,
  podcast: Mic,
  book: BookOpen,
};

function ActivityCard({ activity, currentUserId, onReact, onRemoveReact, onViewItem }) {
  const [showReactions, setShowReactions] = useState(false);
  
  const ActivityIcon = ACTIVITY_ICONS[activity.activity_type] || Eye;
  const MediaIcon = MEDIA_ICONS[activity.media_type] || Tv;
  
  // Get user display name
  const user = activity.user || {};
  const displayName = user.display_name || user.name || 'Someone';
  
  // Get media type label for ranking text
  const getMediaTypeLabel = () => {
    switch (activity.media_type) {
      case 'tv': return 'TV Shows';
      case 'movie': return 'Movies';
      case 'podcast': return 'Podcasts';
      case 'book': return 'Books';
      default: return 'Media';
    }
  };
  
  // Get activity description based on type
  const getActivityDescription = () => {
    const title = activity.item_title;
    
    if (activity.activity_type === 'status_update') {
      const mediaType = activity.media_type === 'tv' ? 'TV' : 
                       activity.media_type === 'movie' ? 'Movie' : 
                       activity.media_type === 'book' ? 'Book' : 'Podcast';
      return { text: `set their ${mediaType} status:`, title: `"${title}"`, suffix: null };
    }
    
    if (activity.activity_type === 'watched' || activity.activity_type === 'listened' || activity.activity_type === 'read') {
      const rank = activity.extra_data?.rank_in_group;
      const total = activity.extra_data?.total_in_group;
      const mediaLabel = getMediaTypeLabel();
      
      if (rank && total) {
        return { text: `ranked`, title, suffix: `as #${rank} of ${total} in ${mediaLabel}` };
      } else if (rank) {
        return { text: `ranked`, title, suffix: `as #${rank} in ${mediaLabel}` };
      }
      const verb = activity.activity_type === 'listened' ? 'listened to' : 
                   activity.activity_type === 'read' ? 'read' : 'watched';
      return { text: verb, title, suffix: null };
    }
    
    if (activity.activity_type === 'added_to_list') {
      return { text: `added`, title, suffix: `to "${activity.extra_data?.list_name || 'a list'}"` };
    }
    
    return { text: 'interacted with', title, suffix: null };
  };
  
  const getGradeIcon = (grade) => {
    switch (grade) {
      case 'liked': return { Icon: ThumbsUp, color: 'text-green-600', bg: 'bg-green-100', label: 'Liked' };
      case 'alright': return { Icon: Meh, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Alright' };
      case 'disliked': return { Icon: ThumbsDown, color: 'text-red-500', bg: 'bg-red-100', label: 'Disliked' };
      default: return null;
    }
  };
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };
  
  const gradeInfo = activity.extra_data?.grade ? getGradeIcon(activity.extra_data.grade) : null;
  const score = activity.extra_data?.calculated_score;
  const comment = activity.extra_data?.comment;
  
  return (
    <Card className="border-0 shadow-md hover:shadow-lg transition-all" data-testid={`activity-${activity.id}`}>
      <CardContent className="p-4">
        {/* Header: User info + time */}
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 border-2 border-[#F87171]/20">
            {user.avatar_url || user.picture ? (
              <AvatarImage src={user.avatar_url || user.picture} alt={displayName} />
            ) : (
              <AvatarFallback className="bg-[#F87171]/10 text-[#F87171]">
                {displayName.charAt(0).toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-semibold text-[#3D4F6F]">{displayName}</span>
              {(() => {
                const desc = getActivityDescription();
                return (
                  <>
                    <span className="text-[#64748B]"> {desc.text} </span>
                    <span className="font-semibold text-[#3D4F6F]">{desc.title}</span>
                    {desc.suffix && <span className="text-[#64748B]"> {desc.suffix}</span>}
                  </>
                );
              })()}
            </p>
            <p className="text-xs text-[#94A3B8] flex items-center gap-1 mt-0.5">
              <Clock className="w-3 h-3" />
              {formatTime(activity.created_at)}
            </p>
          </div>
        </div>
        
        {/* Content: Item card - Different display for status_update */}
        {activity.activity_type === 'status_update' ? (
          <div className="mt-3 p-4 bg-gradient-to-r from-[#F87171]/10 to-[#F87171]/5 rounded-xl">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${
                activity.media_type === 'tv' ? 'bg-blue-100 text-blue-600' :
                activity.media_type === 'movie' ? 'bg-purple-100 text-purple-600' :
                activity.media_type === 'book' ? 'bg-amber-100 text-amber-600' :
                'bg-green-100 text-green-600'
              }`}>
                <MediaIcon className="w-5 h-5" />
              </div>
              <p className="text-[#3D4F6F] font-medium italic">&ldquo;{activity.extra_data?.status_text || activity.item_title}&rdquo;</p>
            </div>
          </div>
        ) : (
          <div 
            className="mt-3 flex gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors"
            onClick={() => onViewItem(activity)}
            data-testid={`activity-item-${activity.id}`}
          >
            {/* Poster/Image */}
            <div className="w-16 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200">
              {activity.item_image ? (
                <img 
                  src={activity.item_image} 
                  alt={activity.item_title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <MediaIcon className="w-6 h-6 text-slate-400" />
                </div>
              )}
            </div>
          
          {/* Item info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className={`p-1 rounded ${
                activity.media_type === 'tv' ? 'bg-blue-100 text-blue-600' :
                activity.media_type === 'movie' ? 'bg-purple-100 text-purple-600' :
                activity.media_type === 'book' ? 'bg-amber-100 text-amber-600' :
                'bg-green-100 text-green-600'
              }`}>
                <MediaIcon className="w-3 h-3" />
              </div>
              <span className="text-xs text-[#64748B] capitalize">{activity.media_type}</span>
            </div>
            <h4 className="font-medium text-[#3D4F6F] mt-1 line-clamp-1">{activity.item_title}</h4>
            
            {/* Score, Grade, and First Watch badges */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {/* Score badge */}
              {score !== null && score !== undefined && (
                <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="text-xs font-medium text-amber-700">{Number(score).toFixed(1)}</span>
                </div>
              )}
              
              {/* Grade badge */}
              {gradeInfo && (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${gradeInfo.bg}`}>
                  <gradeInfo.Icon className={`w-3 h-3 ${gradeInfo.color}`} />
                  <span className={`text-xs font-medium ${gradeInfo.color}`}>{gradeInfo.label}</span>
                </div>
              )}
              
              {/* First watch/listen badge */}
              {(activity.extra_data?.is_first_watch || activity.extra_data?.is_first_listen) && (
                <div className="flex items-center gap-1 bg-purple-50 px-2 py-0.5 rounded-full">
                  <Sparkles className="w-3 h-3 text-purple-500" />
                  <span className="text-xs font-medium text-purple-600">First time!</span>
                </div>
              )}
              
              {/* Big screen badge (movies only) */}
              {activity.extra_data?.is_big_screen && (
                <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full">
                  <Clapperboard className="w-3 h-3 text-amber-500" />
                  <span className="text-xs font-medium text-amber-600">Cinema</span>
                </div>
              )}
            </div>
          </div>
          
          <ChevronRight className="w-5 h-5 text-[#94A3B8] flex-shrink-0 self-center" />
        </div>
        )}
        
        {/* Comment section */}
        {comment && (
          <div className="mt-3 p-3 bg-slate-50 rounded-xl border-l-4 border-[#F87171]">
            <div className="flex items-start gap-2">
              <MessageSquare className="w-4 h-4 text-[#64748B] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#3D4F6F] italic">&ldquo;{comment}&rdquo;</p>
            </div>
          </div>
        )}
        
        {/* Reactions */}
        <div className="mt-3 flex items-center justify-between">
          {/* Reaction counts */}
          <div className="flex items-center gap-2 flex-wrap">
            {activity.reactions && Object.entries(activity.reactions).map(([emoji, count]) => (
              <button
                key={emoji}
                onClick={() => {
                  if (activity.user_reactions?.includes(emoji)) {
                    onRemoveReact(activity.id, emoji);
                  } else {
                    onReact(activity.id, emoji);
                  }
                }}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all ${
                  activity.user_reactions?.includes(emoji)
                    ? 'bg-[#F87171]/20 text-[#F87171]'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`reaction-${emoji}-${activity.id}`}
              >
                <span>{emoji}</span>
                <span className="text-xs font-medium">{count}</span>
              </button>
            ))}
          </div>
          
          {/* Add reaction button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReactions(!showReactions)}
              className="rounded-full text-[#64748B] hover:text-[#3D4F6F]"
              data-testid={`add-reaction-${activity.id}`}
            >
              <span className="text-lg">😊</span>
            </Button>
            
            {showReactions && (
              <div className="absolute right-0 bottom-full mb-2 flex gap-1 bg-white rounded-full shadow-lg p-2 z-10 flex-wrap max-w-[200px]">
                {REACTIONS.map(({ emoji }) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onReact(activity.id, emoji);
                      setShowReactions(false);
                    }}
                    className="text-xl hover:scale-125 transition-transform p-1"
                    data-testid={`react-${emoji}-${activity.id}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBanner({ statuses, currentUserStatus, onUpdateStatus, userInterests }) {
  const [editingType, setEditingType] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [postToFeed, setPostToFeed] = useState(false);
  
  const handleSaveStatus = async (overrideStatus = null, overridePostToFeed = null) => {
    // Use override values if provided, otherwise use state values
    const finalStatus = overrideStatus !== null ? overrideStatus : (statusText || null);
    const finalPostToFeed = overridePostToFeed !== null ? overridePostToFeed : postToFeed;
    
    await onUpdateStatus(editingType, finalStatus, finalPostToFeed);
    setEditingType(null);
    setStatusText('');
    setPostToFeed(false);
  };
  
  const handleClearStatus = async () => {
    // Pass null directly to clear the status, and false for postToFeed
    await onUpdateStatus(editingType, null, false);
    setEditingType(null);
    setStatusText('');
    setPostToFeed(false);
  };
  
  const mediaTypes = [
    { type: 'tv', label: 'TV', icon: Tv, interest: 'tv' },
    { type: 'movie', label: 'Film', icon: Film, interest: 'movies' },
    { type: 'podcast', label: 'Podcast', icon: Mic, interest: 'podcasts' },
    { type: 'book', label: 'Book', icon: BookOpen, interest: 'books' },
  ].filter(m => !userInterests || userInterests.includes(m.interest));
  
  return (
    <div className="space-y-4">
      {/* Current user's status */}
      <div className="bg-gradient-to-r from-[#F87171]/10 to-[#F87171]/5 rounded-xl p-4">
        <h3 className="text-sm font-medium text-[#3D4F6F] mb-3">What are you watching/listening to?</h3>
        <div className="flex flex-wrap gap-2">
          {mediaTypes.map(({ type, label, icon: Icon }) => {
            const statusKey = `${type}_status`;
            const currentStatus = currentUserStatus?.[statusKey];
            
            return (
              <button
                key={type}
                onClick={() => {
                  setEditingType(type);
                  setStatusText(currentStatus || '');
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
                  currentStatus
                    ? 'bg-[#F87171] text-white'
                    : 'bg-white text-[#64748B] hover:bg-slate-100 border border-slate-200'
                }`}
                data-testid={`status-${type}`}
              >
                <Icon className="w-4 h-4" />
                {currentStatus || `Set ${label} status`}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Friends' statuses */}
      {statuses.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-[#3D4F6F]">Friends are watching</h3>
          <div className="flex flex-wrap gap-2">
            {statuses.map((status) => {
              const user = status.user || {};
              const displayName = user.display_name || user.name || 'Friend';
              const activeStatuses = [
                status.tv_status && { type: 'tv', text: status.tv_status, Icon: Tv },
                status.movie_status && { type: 'movie', text: status.movie_status, Icon: Film },
                status.podcast_status && { type: 'podcast', text: status.podcast_status, Icon: Mic },
                status.book_status && { type: 'book', text: status.book_status, Icon: BookOpen },
              ].filter(Boolean);
              
              return activeStatuses.map((s, idx) => (
                <div
                  key={`${status.user_id}-${s.type}`}
                  className="flex items-center gap-2 bg-white rounded-full px-3 py-2 shadow-sm border border-slate-100"
                >
                  <Avatar className="h-6 w-6">
                    {user.avatar_url || user.picture ? (
                      <AvatarImage src={user.avatar_url || user.picture} />
                    ) : (
                      <AvatarFallback className="text-xs bg-[#F87171]/10 text-[#F87171]">
                        {displayName.charAt(0)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <s.Icon className="w-3 h-3 text-[#F87171]" />
                  <span className="text-sm text-[#3D4F6F] max-w-[150px] truncate">{s.text}</span>
                </div>
              ));
            })}
          </div>
        </div>
      )}
      
      {/* Edit status dialog */}
      <Dialog open={!!editingType} onOpenChange={() => setEditingType(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">
              Update your {editingType === 'tv' ? 'TV' : editingType === 'movie' ? 'Film' : editingType === 'book' ? 'Book' : 'Podcast'} status
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Let your friends know what you&apos;re currently watching, listening to, or reading
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Input
              value={statusText}
              onChange={(e) => setStatusText(e.target.value)}
              placeholder={`e.g., "Watching Breaking Bad S3"`}
              className="bg-slate-50"
              data-testid="status-input"
            />
            
            {/* Post to feed checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={postToFeed}
                onChange={(e) => setPostToFeed(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-[#F87171] focus:ring-[#F87171]"
                data-testid="post-to-feed-checkbox"
              />
              <span className="text-sm text-[#64748B]">Post to social feed</span>
            </label>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleClearStatus}
                className="flex-1 rounded-full"
                data-testid="clear-status-btn"
              >
                Clear Status
              </Button>
              <Button
                onClick={() => handleSaveStatus()}
                className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                data-testid="save-status-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SocialPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [friendsStatuses, setFriendsStatuses] = useState([]);
  const [myStatus, setMyStatus] = useState(null);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  
  // Item details dialog state
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Add to list state
  const [showAddToList, setShowAddToList] = useState(false);
  const [watchlists, setWatchlists] = useState([]);
  const [podcastLists, setPodcastLists] = useState([]);
  const [bookLists, setBookLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [addingToList, setAddingToList] = useState(false);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
        setUser(response.data);
      } catch (error) {
        navigate('/');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch social feed
  const fetchFeed = useCallback(async (pageNum = 1, append = false) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams({ page: pageNum, limit: 20 });
      if (mediaFilter !== 'all') params.append('media_type', mediaFilter);
      if (activityFilter !== 'all') params.append('activity_type', activityFilter);
      
      const response = await axios.get(`${API}/social/feed?${params}`, { withCredentials: true, headers });
      
      if (append) {
        setActivities(prev => [...prev, ...response.data.activities]);
      } else {
        setActivities(response.data.activities);
      }
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      // Feed fetch failed - user will see empty state or stale data
    }
  }, [user, mediaFilter, activityFilter]);
  
  // Fetch friends' statuses
  const fetchStatuses = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [friendsRes, myRes] = await Promise.all([
        axios.get(`${API}/social/friends-statuses`, { withCredentials: true, headers }),
        axios.get(`${API}/social/status/me`, { withCredentials: true, headers })
      ]);
      
      setFriendsStatuses(friendsRes.data.statuses || []);
      setMyStatus(myRes.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
      // Status fetch failed - user will see empty status section
    }
  }, [user]);
  
  // Fetch user's watchlists for add-to-list functionality
  const fetchWatchlists = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [tvMovieRes, podcastRes, bookRes] = await Promise.all([
        axios.get(`${API}/watchlists`, { withCredentials: true, headers }),
        axios.get(`${API}/podcast-lists`, { withCredentials: true, headers }),
        axios.get(`${API}/book-lists`, { withCredentials: true, headers })
      ]);
      
      setWatchlists(tvMovieRes.data || []);
      setPodcastLists(podcastRes.data || []);
      setBookLists(bookRes.data || []);
    } catch (error) {
      console.error('Failed to fetch watchlists:', error);
      // Watchlist fetch failed - Add to List will show "No lists" state
    }
  }, [user]);
  
  // Initial load
  useEffect(() => {
    if (user) {
      setLoading(true);
      Promise.all([fetchFeed(1), fetchStatuses(), fetchWatchlists()]).finally(() => setLoading(false));
    }
  }, [user, fetchFeed, fetchStatuses, fetchWatchlists]);
  
  // Reload when filters change
  useEffect(() => {
    if (user) {
      setPage(1);
      fetchFeed(1);
    }
  }, [mediaFilter, activityFilter, user, fetchFeed]);
  
  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    setPage(1);
    await Promise.all([fetchFeed(1), fetchStatuses()]);
    setRefreshing(false);
    toast.success('Feed refreshed!');
  };
  
  // Handle load more
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchFeed(nextPage, true);
  };
  
  // Handle reaction
  const handleReact = async (activityId, emoji) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/social/activities/${activityId}/reactions`, { emoji }, { withCredentials: true, headers });
      
      // Update local state
      setActivities(prev => prev.map(a => {
        if (a.id === activityId) {
          const newReactions = { ...a.reactions };
          newReactions[emoji] = (newReactions[emoji] || 0) + 1;
          return {
            ...a,
            reactions: newReactions,
            user_reactions: [...(a.user_reactions || []), emoji]
          };
        }
        return a;
      }));
    } catch (error) {
      if (error.response?.data?.detail?.includes('Already reacted')) {
        toast.error('You already reacted with this emoji');
      } else {
        toast.error('Failed to add reaction');
      }
    }
  };
  
  // Handle remove reaction
  const handleRemoveReact = async (activityId, emoji) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(`${API}/social/activities/${activityId}/reactions/${encodeURIComponent(emoji)}`, { withCredentials: true, headers });
      
      // Update local state
      setActivities(prev => prev.map(a => {
        if (a.id === activityId) {
          const newReactions = { ...a.reactions };
          newReactions[emoji] = Math.max(0, (newReactions[emoji] || 1) - 1);
          if (newReactions[emoji] === 0) delete newReactions[emoji];
          return {
            ...a,
            reactions: newReactions,
            user_reactions: (a.user_reactions || []).filter(e => e !== emoji)
          };
        }
        return a;
      }));
    } catch (error) {
      toast.error('Failed to remove reaction');
    }
  };
  
  // Handle status update
  const handleUpdateStatus = async (mediaType, status, postToFeed = false) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/social/status`, { 
        media_type: mediaType, 
        status,
        post_to_feed: postToFeed && !!status  // Only post to feed if status is not empty
      }, { withCredentials: true, headers });
      
      // Update local state
      setMyStatus(prev => ({
        ...prev,
        [`${mediaType}_status`]: status
      }));
      
      if (postToFeed && status) {
        toast.success('Status updated and posted to feed!');
        // Refresh feed to show new status post - reset to page 1
        setPage(1);
        fetchFeed(1, false);
      } else {
        toast.success(status ? 'Status updated!' : 'Status cleared');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Handle viewing item details - show details popup
  const handleViewItem = async (activity) => {
    setSelectedActivity(activity);
    setLoadingDetails(true);
    setItemDetails(null);
    
    const { media_type, item_id } = activity;
    const token = localStorage.getItem('auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      let response;
      switch (media_type) {
        case 'tv':
          // Use canonical media API for TV details
          try {
            const details = await getTMDBDetails(item_id, 'tv');
            setItemDetails(details);
            return;
          } catch {
            response = await axios.get(`${API}/tmdb/tv/${item_id}`, { withCredentials: true, headers });
          }
          break;
        case 'movie':
          // Use canonical media API for movie details
          try {
            const details = await getTMDBDetails(item_id, 'movie');
            setItemDetails(details);
            return;
          } catch {
            response = await axios.get(`${API}/tmdb/movie/${item_id}`, { withCredentials: true, headers });
          }
          break;
        case 'podcast':
          // Use canonical media API for podcast details
          try {
            const details = await getPodcastDetails(item_id);
            setItemDetails(details);
            return;
          } catch {
            response = await axios.get(`${API}/podcasts/${item_id}`, { withCredentials: true, headers });
          }
          break;
        case 'book':
          // Use canonical media API for book details
          try {
            const details = await getBookDetails(item_id);
            setItemDetails(details);
            return;
          } catch {
            response = await axios.get(`${API}/books/${item_id}`, { withCredentials: true, headers });
          }
          break;
        default:
          throw new Error('Unknown media type');
      }
      setItemDetails(response.data);
    } catch (error) {
      toast.error('Failed to load details');
      setSelectedActivity(null);
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Close details dialog
  const handleCloseDetails = () => {
    setSelectedActivity(null);
    setItemDetails(null);
  };
  
  // Open add to list dialog
  const handleOpenAddToList = () => {
    setShowAddToList(true);
    setSelectedListId(null);
  };
  
  // Close add to list dialog
  const handleCloseAddToList = () => {
    setShowAddToList(false);
    setSelectedListId(null);
  };
  
  // Add item to list
  const handleAddToList = async (position = 'bottom') => {
    if (!selectedListId || !selectedActivity || !itemDetails) return;
    
    const { media_type } = selectedActivity;
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    
    setAddingToList(true);
    
    try {
      if (media_type === 'podcast') {
        // Add podcast to podcast list
        await axios.post(`${API}/podcast-lists/${selectedListId}/podcasts`, {
          podcast_id: itemDetails.podcast_id || itemDetails.id,
          title: itemDetails.title,
          publisher: itemDetails.publisher,
          thumbnail: itemDetails.thumbnail || itemDetails.image,
          genres: itemDetails.genres || [],
          position
        }, { withCredentials: true, headers });
      } else if (media_type === 'book') {
        // Add book to book list
        await axios.post(`${API}/book-lists/${selectedListId}/books`, {
          book_id: itemDetails.book_id || itemDetails.id,
          title: itemDetails.title,
          author: itemDetails.author,
          cover_image: itemDetails.cover_image,
          description: itemDetails.description,
          page_count: itemDetails.page_count || 0,
          genres: itemDetails.genres || [],
          position
        }, { withCredentials: true, headers });
      } else {
        // Add TV/movie to watchlist
        await axios.post(`${API}/watchlists/${selectedListId}/series`, {
          tmdb_id: itemDetails.tmdb_id,
          title: itemDetails.title,
          poster_path: itemDetails.poster_path,
          first_air_date: itemDetails.first_air_date,
          genres: itemDetails.genres || [],
          vote_average: itemDetails.vote_average,
          media_type: media_type,
          position
        }, { withCredentials: true, headers });
      }
      
      toast.success(`Added "${itemDetails.title}" to list!`);
      handleCloseAddToList();
      handleCloseDetails();
      fetchWatchlists(); // Refresh lists
    } catch (error) {
      if (error.response?.data?.detail?.includes('already in')) {
        toast.error('This item is already in the list');
      } else {
        toast.error('Failed to add to list');
      }
    } finally {
      setAddingToList(false);
    }
  };
  
  // Get appropriate lists for the current media type
  const getListsForMediaType = () => {
    if (!selectedActivity) return [];
    const { media_type } = selectedActivity;
    
    if (media_type === 'podcast') {
      return podcastLists;
    } else if (media_type === 'book') {
      return bookLists;
    } else {
      return watchlists.filter(l => l.media_category === media_type);
    }
  };
  
  if (!user) return null;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EEF2FF]">
      <AppHeader user={user} />
      
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3D4F6F]">Sorta Social</h1>
            <p className="text-sm text-[#64748B]">See what your friends are watching</p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-full"
            data-testid="refresh-feed-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Status Banner */}
        <StatusBanner
          statuses={friendsStatuses}
          currentUserStatus={myStatus}
          onUpdateStatus={handleUpdateStatus}
          userInterests={user?.interests}
        />
        
        {/* Filters */}
        <div className="flex gap-3 mt-6 mb-4">
          <Select value={mediaFilter} onValueChange={setMediaFilter}>
            <SelectTrigger className="w-[140px] bg-white rounded-full" data-testid="media-filter">
              <SelectValue placeholder="All Media" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Media</SelectItem>
              <SelectItem value="tv">TV Shows</SelectItem>
              <SelectItem value="movie">Films</SelectItem>
              <SelectItem value="podcast">Podcasts</SelectItem>
              <SelectItem value="book">Books</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={activityFilter} onValueChange={setActivityFilter}>
            <SelectTrigger className="w-[140px] bg-white rounded-full" data-testid="activity-filter">
              <SelectValue placeholder="All Activity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activity</SelectItem>
              <SelectItem value="watched">Watched</SelectItem>
              <SelectItem value="listened">Listened</SelectItem>
              <SelectItem value="read">Read</SelectItem>
              <SelectItem value="added_to_list">Added to List</SelectItem>
              <SelectItem value="status_update">Status Updates</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Activity Feed */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-[#F87171]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-[#F87171]" />
            </div>
            <h3 className="font-medium text-[#3D4F6F] mb-2">No activity yet</h3>
            <p className="text-[#64748B] text-sm mb-4">
              Follow some friends to see their activity here, or start watching shows!
            </p>
            <Button
              onClick={() => navigate('/profile')}
              className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
            >
              Find Friends
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                currentUserId={user.user_id}
                onReact={handleReact}
                onRemoveReact={handleRemoveReact}
                onViewItem={handleViewItem}
              />
            ))}
            
            {hasMore && (
              <div className="text-center pt-4">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  className="rounded-full"
                  data-testid="load-more-btn"
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Item Details Dialog */}
      <Dialog open={!!selectedActivity} onOpenChange={handleCloseDetails}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="social-item-details-dialog">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
            </div>
          ) : itemDetails && (
            <>
              <div className="flex gap-6">
                {/* Poster/Thumbnail */}
                {(itemDetails.poster_path || itemDetails.thumbnail || itemDetails.image) ? (
                  <img 
                    src={itemDetails.poster_path || itemDetails.thumbnail || itemDetails.image} 
                    alt={itemDetails.title} 
                    className="w-32 h-48 object-cover rounded-xl shadow-lg flex-shrink-0" 
                  />
                ) : (
                  <div className="w-32 h-48 bg-slate-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    {selectedActivity?.media_type === 'podcast' ? (
                      <Mic className="w-12 h-12 text-slate-400" />
                    ) : selectedActivity?.media_type === 'movie' ? (
                      <Film className="w-12 h-12 text-slate-400" />
                    ) : (
                      <Tv className="w-12 h-12 text-slate-400" />
                    )}
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h2 className="font-outfit text-xl sm:text-2xl font-bold text-[#3D4F6F]">{itemDetails.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedActivity?.media_type === 'podcast' 
                        ? 'bg-green-100 text-green-700'
                        : selectedActivity?.media_type === 'movie' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                    }`}>
                      {selectedActivity?.media_type === 'podcast' ? 'Podcast' : selectedActivity?.media_type === 'movie' ? 'Movie' : 'TV'}
                    </span>
                  </div>
                  
                  {itemDetails.tagline && <p className="text-[#64748B] italic mb-3">{itemDetails.tagline}</p>}
                  {itemDetails.publisher && <p className="text-[#64748B] text-sm mb-3">By {itemDetails.publisher}</p>}
                  
                  {/* Genres */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {itemDetails.genres?.map((genre, i) => (
                      <Badge key={i} variant="secondary" className="bg-[#F87171]/10 text-[#F87171]">
                        {typeof genre === 'string' ? genre : genre.name}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Info row */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {(itemDetails.first_air_date || itemDetails.release_date) && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Calendar className="w-4 h-4" />
                        {new Date(itemDetails.first_air_date || itemDetails.release_date).getFullYear()}
                      </div>
                    )}
                    {itemDetails.vote_average > 0 && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        {itemDetails.vote_average.toFixed(1)}
                      </div>
                    )}
                    {itemDetails.number_of_seasons && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Tv className="w-4 h-4" />
                        {itemDetails.number_of_seasons} season{itemDetails.number_of_seasons > 1 ? 's' : ''}
                      </div>
                    )}
                    {itemDetails.runtime && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Clock className="w-4 h-4" />
                        {itemDetails.runtime}
                      </div>
                    )}
                    {itemDetails.total_episodes && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Headphones className="w-4 h-4" />
                        {itemDetails.total_episodes} episodes
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Overview */}
              {(itemDetails.overview || itemDetails.description) && (
                <div className="mt-4">
                  <h3 className="font-medium text-[#3D4F6F] mb-2">Overview</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    {itemDetails.overview || itemDetails.description}
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="mt-6 flex gap-3">
                <Button
                  onClick={handleOpenAddToList}
                  className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                  data-testid="add-to-list-btn"
                >
                  <ListPlus className="w-4 h-4 mr-2" />
                  Add to List
                </Button>
                {itemDetails.imdb_url && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(itemDetails.imdb_url, '_blank')}
                    className="rounded-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    IMDb
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add to List Dialog */}
      <Dialog open={showAddToList} onOpenChange={handleCloseAddToList}>
        <DialogContent className="max-w-md" data-testid="social-add-to-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">Add to List</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Choose a list for &ldquo;{itemDetails?.title}&rdquo;
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {getListsForMediaType().length === 0 ? (
              <div className="text-center py-6">
                <List className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-[#64748B] mb-4">No lists yet for this media type</p>
                <Button
                  onClick={() => {
                    handleCloseAddToList();
                    handleCloseDetails();
                    navigate(selectedActivity?.media_type === 'podcast' ? '/podcasts' : 
                             selectedActivity?.media_type === 'movie' ? '/films' : '/tv-shows');
                  }}
                  className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                >
                  Create a List
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2 max-h-[200px] overflow-y-auto mb-4">
                  {getListsForMediaType().map((list) => {
                    const itemCount = selectedActivity?.media_type === 'podcast' 
                      ? list.podcasts?.length || 0
                      : list.series?.length || 0;
                    
                    return (
                      <button
                        key={list.id}
                        onClick={() => setSelectedListId(list.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          selectedListId === list.id
                            ? 'border-[#F87171] bg-[#F87171]/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`list-option-${list.id}`}
                      >
                        <List className={`w-5 h-5 ${selectedListId === list.id ? 'text-[#F87171]' : 'text-slate-400'}`} />
                        <div className="flex-1 text-left">
                          <p className={`font-medium ${selectedListId === list.id ? 'text-[#F87171]' : 'text-[#3D4F6F]'}`}>
                            {list.title}
                          </p>
                          <p className="text-xs text-[#64748B]">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
                        </div>
                        {selectedListId === list.id && <Check className="w-5 h-5 text-[#F87171]" />}
                      </button>
                    );
                  })}
                </div>
                
                {/* Position buttons */}
                {selectedListId && (
                  <div className="space-y-2 pt-4 border-t border-slate-100">
                    <p className="text-sm font-medium text-[#64748B] mb-2">Where to add?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => handleAddToList('top')}
                        disabled={addingToList}
                        variant="outline"
                        className="rounded-full"
                        data-testid="add-to-top-btn"
                      >
                        <ArrowUp className="w-4 h-4 mr-2" />
                        Add to Top
                      </Button>
                      <Button
                        onClick={() => handleAddToList('bottom')}
                        disabled={addingToList}
                        variant="outline"
                        className="rounded-full"
                        data-testid="add-to-bottom-btn"
                      >
                        <ArrowDown className="w-4 h-4 mr-2" />
                        Add to Bottom
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
